import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { getAsset } from '../assets/catalog'
import type { AssetDefinition } from '../assets/types'
import { Outlined } from '../render/Outlined'
import { boxGeo } from '../render/geometry'
import { OUTLINE } from '../render/theme'
import {
  rotatedFootprint,
  rotationRadians,
  WALL_YAW,
  type PlacedItem as PlacedItemData,
  type RoomConfig,
  type RotationSteps,
  type WallSide,
} from '../state/document'
import { helperProps } from './helpers'
import { registerItem, unregisterItem } from './itemRegistry'

/* ------------------------------------------------------------ placement */

/** Where an item's group sits in world space, including wall mounting. */
export function itemTransform(
  item: Pick<PlacedItemData, 'x' | 'z' | 'y' | 'rotation' | 'wall'>,
  asset: AssetDefinition,
  room: RoomConfig,
): { position: [number, number, number]; yaw: number } {
  if (asset.anchor === 'wall' && item.wall) {
    const y = item.y || asset.mountHeight || 1.5
    const side: WallSide = item.wall
    const position: [number, number, number] =
      side === 'north'
        ? [item.x, y, -room.depth / 2]
        : side === 'south'
          ? [item.x, y, room.depth / 2]
          : side === 'west'
            ? [-room.width / 2, y, item.z]
            : [room.width / 2, y, item.z]
    return { position, yaw: WALL_YAW[side] }
  }
  return { position: [item.x, item.y, item.z], yaw: rotationRadians(item.rotation) }
}

/* -------------------------------------------------------------- visuals */

export interface AssetInstanceProps {
  assetId: string
  variant?: string
  accent?: string
  outlineColor?: string
  outlineWidth?: number
  /** Contour-only rendering, for the placement ghost. */
  outlineOnly?: boolean
}

/**
 * Renders one catalog asset with its merged contour. Used by the scene, by the
 * placement ghost and by the catalog thumbnail forge, so all three are
 * pixel-identical by construction.
 */
export function AssetInstance({
  assetId,
  variant,
  accent,
  outlineColor,
  outlineWidth,
  outlineOnly,
}: AssetInstanceProps) {
  const asset = getAsset(assetId)
  if (!asset) return null
  const Component = asset.Component
  return (
    <Outlined color={outlineColor} width={outlineWidth} contentVisible={!outlineOnly}>
      <Component variant={variant} accent={accent} />
    </Outlined>
  )
}

/** Invisible pick target sized to the asset's footprint. */
function HitBox({ uid, asset }: { uid: string; asset: AssetDefinition }) {
  const ref = useRef<THREE.Mesh>(null)

  useEffect(() => {
    registerItem(uid, ref.current)
    return () => unregisterItem(uid)
  }, [uid])

  const [w, d] = asset.footprint
  const wall = asset.anchor === 'wall'
  const geometry = useMemo(
    () => boxGeo(Math.max(w, 0.08), Math.max(asset.height, 0.08), Math.max(d, 0.08)),
    [w, d, asset.height],
  )

  return (
    <mesh
      ref={ref}
      geometry={geometry}
      visible={false}
      position={wall ? [0, 0, d / 2] : [0, asset.height / 2, 0]}
    >
      <meshBasicMaterial />
    </mesh>
  )
}

/** Footprint ring drawn on the floor under the selected or hovered item. */
function FootprintRing({
  footprint,
  rotation,
  color,
  y,
}: {
  footprint: readonly [number, number]
  rotation: RotationSteps
  color: string
  y: number
}) {
  const geometry = useMemo(() => {
    const [w, d] = rotatedFootprint(footprint, rotation)
    const hw = w / 2 + 0.03
    const hd = d / 2 + 0.03
    const corners: [number, number][] = [
      [-hw, -hd],
      [hw, -hd],
      [hw, hd],
      [-hw, hd],
    ]
    const points: number[] = []
    for (let i = 0; i < 4; i += 1) {
      const [x1, z1] = corners[i]!
      const [x2, z2] = corners[(i + 1) % 4]!
      points.push(x1, 0, z1, x2, 0, z2)
    }
    const buffer = new THREE.BufferGeometry()
    buffer.setAttribute('position', new THREE.Float32BufferAttribute(points, 3))
    return buffer
  }, [footprint, rotation])

  return (
    <lineSegments geometry={geometry} position={[0, y + 0.006, 0]} {...helperProps} raycast={() => {}}>
      <lineBasicMaterial color={color} toneMapped={false} />
    </lineSegments>
  )
}

/* ---------------------------------------------------------------- item */

export interface PlacedItemProps {
  item: PlacedItemData
  room: RoomConfig
  accent: string
  selected: boolean
  hovered: boolean
}

export function PlacedItem({ item, room, accent, selected, hovered }: PlacedItemProps) {
  const asset = getAsset(item.assetId)
  if (!asset) return null

  const { position, yaw } = itemTransform(item, asset, room)

  return (
    <group position={position} rotation={[0, yaw, 0]}>
      <AssetInstance
        assetId={item.assetId}
        variant={item.variant ?? asset.variants?.[0]?.id}
        accent={item.accent ?? accent}
        outlineColor={selected ? OUTLINE.selectedColor : undefined}
        outlineWidth={selected ? OUTLINE.selectedWidth : undefined}
      />
      <HitBox uid={item.uid} asset={asset} />
      {(selected || hovered) && asset.anchor !== 'wall' ? (
        <FootprintRing
          footprint={asset.footprint}
          rotation={0}
          color={selected ? OUTLINE.selectedColor : '#b6bec9'}
          y={-item.y}
        />
      ) : null}
    </group>
  )
}

/* --------------------------------------------------------------- ghost */

export interface GhostProps {
  assetId: string
  variant?: string
  rotation: RotationSteps
  x: number
  z: number
  y: number
  wall?: WallSide
  valid: boolean
  room: RoomConfig
}

export function PlacementGhost({ assetId, variant, rotation, x, z, y, wall, valid, room }: GhostProps) {
  const asset = getAsset(assetId)
  if (!asset) return null
  const { position, yaw } = itemTransform({ x, z, y, rotation, wall }, asset, room)
  const color = valid ? OUTLINE.selectedColor : '#e5484d'

  return (
    <group position={position} rotation={[0, yaw, 0]} {...helperProps}>
      <AssetInstance
        assetId={assetId}
        variant={variant}
        outlineColor={color}
        outlineWidth={OUTLINE.selectedWidth}
        outlineOnly
      />
      {asset.anchor !== 'wall' ? (
        <FootprintRing footprint={asset.footprint} rotation={0} color={color} y={-y} />
      ) : null}
    </group>
  )
}
